import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import {
  AdminOverview,
  AdminOverviewService,
} from '../../../../core/services/admin-overview.service';
import { NotificationService } from '../../../../core/services/notifications/notification.service';
import { NotificationDto } from '../../../../core/interfaces/notification';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import { IdentityBannerComponent } from '../../../ui/views/identity-banner/identity-banner.component';
import { AuthService } from '../../../../core/services/utils/auth.service';

/** Une tuile du bandeau de chiffres. */
interface OverviewTile {
  labelKey: string;
  value: number;
  icon: string;
  link: string;
  /** La permission qu'exige la route visée — la même que dans `app.routes.ts`. */
  permission: string;
  /** Met la tuile en évidence quand elle appelle une action. */
  alert?: boolean;
}

/** Une tuile, et si ce compte peut ouvrir l'écran où elle mène. */
type ShownTile = OverviewTile & { open: boolean };

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    NgTemplateOutlet,
    RouterLink,
    TranslateModule,
    ErrorStateComponent,
    IdentityBannerComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit {
  private readonly overviewService = inject(AdminOverviewService);
  private readonly notificationService = inject(NotificationService);
  private readonly auth = inject(AuthService);

  currentUserName = '';
  currentUserAvatar = 'assets/images/default-user.png';

  readonly overview = signal<AdminOverview | null>(null);
  readonly loading = signal(true);
  readonly errored = signal(false);

  readonly notifications = signal<NotificationDto[]>([]);
  readonly notificationsLoading = signal(true);

  /** Effectifs de l'établissement. */
  readonly people = computed<ShownTile[]>(() => {
    const o = this.overview();
    if (!o) return [];
    return this.shown([
      { labelKey: 'ADMIN_HOME.STUDENTS', value: o.activeStudents, icon: 'fa-solid fa-user-graduate', link: '/admin/students', permission: 'students.read.all' },
      { labelKey: 'ADMIN_HOME.PROFESSORS', value: o.activeProfessors, icon: 'fa-solid fa-chalkboard-user', link: '/admin/professors', permission: 'professors.read' },
      { labelKey: 'ADMIN_HOME.SUBJECTS', value: o.activeSubjects, icon: 'fa-solid fa-book', link: '/admin/learning-paths', permission: 'referential.read' },
      { labelKey: 'ADMIN_HOME.GROUPS', value: o.activeGroups, icon: 'fa-solid fa-users', link: '/admin/groups', permission: 'groups.read' },
    ]);
  });

  /** Ce qui demande une action ou un suivi. */
  readonly activity = computed<ShownTile[]>(() => {
    const o = this.overview();
    if (!o) return [];
    return this.shown([
      { labelKey: 'ADMIN_HOME.SESSIONS_TODAY', value: o.sessionsToday, icon: 'fa-solid fa-calendar-day', link: '/admin/calendar', permission: 'sessions.read.all' },
      { labelKey: 'ADMIN_HOME.SESSIONS_WEEK', value: o.sessionsNextSevenDays, icon: 'fa-solid fa-calendar-week', link: '/admin/calendar', permission: 'sessions.read.all' },
      {
        labelKey: 'ADMIN_HOME.SESSIONS_TO_REVIEW',
        value: o.sessionsToReview,
        icon: 'fa-solid fa-triangle-exclamation',
        link: '/admin/calendar',
        permission: 'sessions.read.all',
        alert: o.sessionsToReview > 0,
      },
      {
        labelKey: 'ADMIN_HOME.PENDING_REGISTRATIONS',
        value: o.pendingRegistrations,
        icon: 'fa-solid fa-file-signature',
        link: '/admin/registrations',
        permission: 'registrations.read',
        alert: o.pendingRegistrations > 0,
      },
    ]);
  });

  /**
   * Le chiffre reste — le tableau de bord est ouvert à tout administrateur, et
   * un compteur n'expose personne. Ce qui se retire, c'est le lien : une tuile
   * qui mènerait à `/unauthorized` promet un écran que le compte n'a pas.
   */
  private shown(tiles: OverviewTile[]): ShownTile[] {
    return tiles.map((t) => ({ ...t, open: this.auth.hasPermission(t.permission) }));
  }

  ngOnInit(): void {
    this.loadIdentity();
    this.load();
    this.loadNotifications();
  }

  private async loadIdentity(): Promise<void> {
    try {
      this.currentUserName = await this.auth.getUserDisplayName();
      this.currentUserAvatar = await this.auth.getUserAvatar();
    } catch {
      // Le bandeau n'est pas critique : sans nom il reste discret plutôt
      // que d'afficher un libellé inventé.
      this.currentUserName = '';
    }
  }

  reload(): void {
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.errored.set(false);

    this.overviewService.getOverview().subscribe({
      next: (res) => {
        this.overview.set(res?.data ?? null);
        this.loading.set(false);
      },
      error: () => {
        // Aucun chiffre de substitution : l'écran affiche son état d'erreur.
        this.overview.set(null);
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }

  private loadNotifications(): void {
    this.notificationsLoading.set(true);
    this.notificationService.list(0, 6).subscribe({
      next: (res) => {
        this.notifications.set(res?.data?.items ?? []);
        this.notificationsLoading.set(false);
      },
      error: () => {
        this.notifications.set([]);
        this.notificationsLoading.set(false);
      },
    });
  }
}
